import React, { useEffect, useState } from "react";
import { Save, Plus, Trash2, RefreshCw, Users, Building2, BadgeCheck, Database, UserCircle2, Calculator, ArrowRight, Lock, BookOpen, CloudOff, CalendarRange, KeyRound } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton, UserAvatar } from "@/components/common";
import { LosLogo } from "@/components/Logo";
import { BarAction, PageBar } from "@/components/layout/PageBar";
import { longDate } from "@/lib/sk";
import { ChangePasswordCard } from "@/components/settings/ChangePasswordCard";
import { ProfileCard } from "@/components/settings/ProfileCard";
import { TeamDirectory } from "@/components/settings/TeamDirectory";
import { AccessOverview } from "@/components/settings/AccessOverview";
import { M365Setup } from "@/components/settings/m365/M365Setup";
import { useResource } from "@/hooks/use-resource";

const LEVEL = { none: "Ingen", sales_manager: "Salgssjef", management: "Ledelse" };
const ROLE = { Sales: "Salg", Engineering: "Ingeniør", "Sales manager": "Salgssjef", Management: "Ledelse" };

function Section({ icon: Icon, title, description, children, testId }) {
  return (
    <section id={testId} className="scroll-mt-6 grid grid-cols-1 gap-5 border-t border-black/[0.08] py-8 lg:grid-cols-[260px_minmax(0,1fr)]" data-testid={testId}>
      <div><h2 className="flex items-center gap-2 text-[15px] font-semibold"><Icon className="h-4 w-4 text-muted-foreground" strokeWidth={1.9} /> {title}</h2>{description ? <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">{description}</p> : null}</div>
      <div className="min-w-0">{children}</div>
    </section>
  );
}

function UsersLink() {
  const { data } = useResource("/brukere/status");
  const st = data?.stats;
  const line = st ? [`${st.active} aktive`, st.must_change ? `${st.must_change} venter på innlogging` : null, st.inactive ? `${st.inactive} deaktivert` : null].filter(Boolean).join(" · ") : "Laster…";
  return (
    <Link to="/settings/brukere" className="group flex items-center justify-between gap-4 rounded-[16px] border border-black/[0.06] bg-white p-4 transition-colors hover:border-black/[0.2]" data-testid="settings-users-link">
      <div className="flex min-w-0 items-center gap-3">
        {data?.admins?.length ? <div className="hidden shrink-0 -space-x-1.5 sm:flex">{data.admins.slice(0, 3).map((a) => <UserAvatar key={a.id} name={a.name} initials={a.initials} color={a.color} src={a.avatar_url} size="md" className="h-7 w-7 text-[10px] ring-2 ring-white" />)}</div> : null}
        <div className="min-w-0 text-[13px]">
          <div className="font-medium">{data?.can_admin ? "Administrer brukere" : "Se hvem som administrerer brukere"}</div>
          <div className="mt-0.5 text-[12px] text-muted-foreground" data-testid="settings-users-summary">{line}</div>
          {data && !data.can_admin ? <div className="mt-1 inline-flex items-center gap-1 text-[11.5px] text-muted-foreground"><Lock className="h-3 w-3" /> Endringer gjøres av en brukeradministrator</div> : null}
        </div>
      </div>
      <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
    </Link>
  );
}

const Card = ({ className = "", children, ...p }) => <div className={`rounded-[16px] border border-black/[0.06] bg-white ${className}`} {...p}>{children}</div>;

/** Deliberately small: who you are, the company on the quote, approval thresholds, the team, demo data. */
export default function SettingsPage() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reseeding, setReseeding] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [rates, setRates] = useState(null);
  const [texts, setTexts] = useState(null);

  const load = () => Promise.all([api.get("/settings"), api.get("/los-settings"), api.get("/tilbudstekster")]).then(([a, c, d]) => { setS(a.data); setRates(c.data); setTexts(d.data); setDirty(false); }).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const upd = (patch) => { setS((cur) => ({ ...cur, ...patch })); setDirty(true); };
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/settings", { approval_rules: s.approval_rules, discount_threshold: Number(s.discount_threshold), company: s.company });
      setS(r.data);
      setDirty(false);
      toast.success("Innstillinger lagret");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };
  const reseed = async () => {
    if (!window.confirm("Tømme arbeidsområdet? Alle prosjekter, kunder, tilbud og dokumenter slettes. Brukere og firmaoppsett beholdes.")) return;
    setReseeding(true);
    try {
      await api.post("/admin/reseed");
      toast.success("Arbeidsområdet er tømt");
      window.dispatchEvent(new Event("projects:changed"));
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setReseeding(false);
    }
  };
  const company = s?.company || {};
  const setCo = (k) => (e) => upd({ company: { ...company, [k]: e.target.value } });

  return (
    <div className="mx-auto w-full max-w-[1000px] px-5 pb-16 pt-5 sm:px-8" data-testid="settings-page">
      <PageBar title="Innstillinger" testId="settings-bar" className="pb-2" action={dirty ? <BarAction primary onClick={save} testId="settings-save-button"><Save className="h-4 w-4" /> {saving ? "Lagrer…" : "Lagre endringer"}</BarAction> : null} />
      {!s ? <div className="space-y-4"><Skeleton className="h-24" /><Skeleton className="h-40" /></div> : (
        <div>
          <Section icon={UserCircle2} title="Deg" description="Navn, bilde og passord." testId="settings-profile">
            <div className="space-y-3">
              <ProfileCard />
              <ChangePasswordCard />
            </div>
          </Section>

          <Section icon={Building2} title="Selskap" description="Slik selskapet står på tilbudet." testId="settings-company">
            <Card className="space-y-4 p-4">
              <div className="flex items-center justify-between"><LosLogo className="text-[18px]" /><span className="text-[12px] text-muted-foreground">{company.legal_name || "LOS Marine AS"}</span></div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {[["Slagord", "tagline"], ["Salgs-e-post", "email"], ["Telefon", "phone"], ["Adresse", "address"], ["Nettsted", "website"], ["Juridisk navn", "legal_name"]].map(([l, k]) => (
                  <div key={k} className="space-y-1.5"><Label className="text-[12.5px]">{l}</Label><Input value={company[k] || ""} onChange={setCo(k)} className="h-10 rounded-[10px]" data-testid={`settings-company-${k}-input`} /></div>
                ))}
              </div>
            </Card>
          </Section>

          <Section icon={Calculator} title="Kalkyle & satser" description="Timesatser, prisliste og regler – versjonert." testId="settings-rates">
            <Link to="/settings/satser" className="group flex items-center justify-between gap-4 rounded-[16px] border border-black/[0.06] bg-white p-4 transition-colors hover:border-black/[0.2]" data-testid="settings-rates-link">
              <div className="min-w-0 text-[13px]">
                <div className="font-medium">Åpne Kalkyle & satser</div>
                <div className="mt-0.5 text-[12px] text-muted-foreground">{rates ? `Aktiv versjon v${rates.active?.version} · ${rates.active?.data ? rates.active.data.labor_rates.length : "–"} timesatser · ${rates.active?.data ? rates.active.data.pricelist.lines.length : "–"} prislistelinjer${rates.draft ? ` · kladd v${rates.draft.version} finnes` : ""}` : "Laster…"}</div>
                {rates && !rates.can_write ? <div className="mt-1 inline-flex items-center gap-1 text-[11.5px] text-[hsl(var(--v-assumed))]"><Lock className="h-3 w-3" /> Lagring sperret – eksplisitt adminrett mangler</div> : null}
              </div>
              <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
            </Link>
          </Section>

          <Section icon={BookOpen} title="Tilbudstekster" description="Følgebrev, forbehold og vilkår – biblioteket tilbudene henter fra." testId="settings-texts">
            <Link to="/settings/tekster" className="group flex items-center justify-between gap-4 rounded-[16px] border border-black/[0.06] bg-white p-4 transition-colors hover:border-black/[0.2]" data-testid="settings-texts-link">
              <div className="min-w-0 text-[13px]">
                <div className="font-medium">Åpne Tilbudstekster</div>
                <div className="mt-0.5 text-[12px] text-muted-foreground">{texts ? `${texts.sections.reduce((n, s) => n + s.templates.filter((t) => !t.archived_at).length, 0)} tekster · ${texts.sections.filter((s) => s.standard).length} standard satt · ${texts.sections.flatMap((s) => s.templates).filter((t) => t.status === "historisk" && !t.archived_at).length} historiske kildetekster` : "Laster…"}</div>
                {texts && !texts.can_write ? <div className="mt-1 inline-flex items-center gap-1 text-[11.5px] text-[hsl(var(--v-assumed))]"><Lock className="h-3 w-3" /> Lagring sperret – eksplisitt adminrett mangler</div> : null}
              </div>
              <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
            </Link>
          </Section>

          <Section icon={CalendarRange} title="Plan & kapasitet" description="Avdelingenes timer per uke, maskiner og fasiliteter, kalender og regler bak kapasitetskontrollen i tilbud." testId="settings-plan">
            <Link to="/settings/plan" className="group flex items-center justify-between gap-4 rounded-[16px] border border-black/[0.06] bg-white p-4 transition-colors hover:border-black/[0.2]" data-testid="settings-plan-link">
              <div className="min-w-0 text-[13px]">
                <div className="font-medium">Åpne Plan & kapasitet</div>
                <div className="mt-0.5 text-[12px] text-muted-foreground">Netto t/uke per avdeling · skift på maskiner · dokk, slipp og kai · helligdager og unntak · sannsynlighet og terskler · demo-data</div>
              </div>
              <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
            </Link>
          </Section>

          <Section icon={BadgeCheck} title="Godkjenningsregler" description="Hvem som må godkjenne prisen før sending." testId="settings-approval">
            <Card className="p-4">
              <div className="space-y-2">
                {s.approval_rules.map((r, i) => (
                  <div key={i} className="flex flex-wrap items-center gap-2 text-[13px]" data-testid="settings-approval-rule-row">
                    <span className="text-muted-foreground">DB fra</span><Input type="number" value={r.min_gm} onChange={(e) => upd({ approval_rules: s.approval_rules.map((x, j) => (j === i ? { ...x, min_gm: Number(e.target.value) } : x)) })} className="h-8 w-20 rounded-lg text-right tabular-nums" data-testid="settings-approval-min-input" />
                    <span className="text-muted-foreground">% til</span><Input type="number" value={r.max_gm} onChange={(e) => upd({ approval_rules: s.approval_rules.map((x, j) => (j === i ? { ...x, max_gm: Number(e.target.value) } : x)) })} className="h-8 w-20 rounded-lg text-right tabular-nums" data-testid="settings-approval-max-input" />
                    <span className="text-muted-foreground">% krever</span>
                    <select value={r.level} onChange={(e) => upd({ approval_rules: s.approval_rules.map((x, j) => (j === i ? { ...x, level: e.target.value, label: `${LEVEL[e.target.value]}godkjenning` } : x)) })} className="h-8 rounded-lg border border-black/[0.1] bg-white px-2 text-[13px]" data-testid="settings-approval-level-select">
                      {Object.entries(LEVEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </select>
                    {s.approval_rules.length > 1 ? <button type="button" onClick={() => upd({ approval_rules: s.approval_rules.filter((_, j) => j !== i) })} className="rounded p-1 text-muted-foreground hover:text-destructive" aria-label="Fjern regel" data-testid="settings-approval-delete-button"><Trash2 className="h-3.5 w-3.5" /></button> : null}
                  </div>
                ))}
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <button type="button" onClick={() => upd({ approval_rules: [...s.approval_rules, { min_gm: 0, max_gm: 20, level: "management", label: "Ledelsesgodkjenning" }] })} className="inline-flex h-8 items-center gap-1 rounded-full border border-black/[0.1] bg-white px-3 text-[12.5px] font-medium hover:border-black/[0.25]" data-testid="settings-approval-add-button"><Plus className="h-3.5 w-3.5" /> Regel</button>
                <div className="ml-auto flex items-center gap-2 text-[13px]"><span className="text-muted-foreground">Linjerabatt over</span><Input type="number" value={s.discount_threshold} onChange={(e) => upd({ discount_threshold: e.target.value })} className="h-8 w-16 rounded-lg text-right tabular-nums" data-testid="settings-discount-threshold-input" /><span className="text-muted-foreground">% krever salgssjef</span></div>
              </div>
            </Card>
          </Section>

          <Section icon={KeyRound} title="Brukere" description="Hvem som kan logge inn – roller, passord og adminrettigheter." testId="settings-users">
            <UsersLink />
          </Section>

          <Section icon={Users} title="Team" description="Hvem som kan tildeles arbeid og nevnes." testId="settings-team">
            <TeamDirectory />
          </Section>
          <Section icon={Lock} title="Tilgang og roller" description="Hva hver person kan gjøre – lesevisning." testId="settings-access">
            <AccessOverview />
          </Section>
          <Section icon={CloudOff} title="Integrasjoner" description="Microsoft 365 – oppsett for en senere kobling. Ikke tilkoblet." testId="settings-integrations">
            <M365Setup />
          </Section>

          <Section icon={Database} title="Arbeidsområde" description="Start på nytt." testId="settings-demo">
            <Card className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-[13px]"><div className="font-medium">Tøm arbeidsområdet</div><div className="text-[12px] text-muted-foreground">Sletter alle saker, arbeidsposter, kunder og dokumentreferanser. Brukere og firmaoppsett beholdes.{rates && !rates.can_write ? " Krever eksplisitt adminrett (sperret)." : ""}</div></div>
              <button type="button" onClick={reseed} disabled={reseeding || (rates && !rates.can_write)} className="inline-flex h-9 shrink-0 items-center gap-2 rounded-full border border-black/[0.1] bg-white px-3.5 text-[13px] font-medium hover:border-black/[0.25] disabled:opacity-40" data-testid="settings-reseed-button"><RefreshCw className={reseeding ? "h-4 w-4 animate-spin" : "h-4 w-4"} /> {reseeding ? "Tømmer…" : "Tøm arbeidsområdet"}</button>
            </Card>
            {s.updated_at ? <p className="mt-2 text-[12px] text-muted-foreground">Sist lagret {longDate(s.updated_at)}.</p> : null}
          </Section>
        </div>
      )}
    </div>
  );
}
